import React, { useEffect, useState } from "react";
import { PaymentApi } from "api";
import { PaziaModal } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { useKsefXml } from "api/payments/useKsefXml";
import { paymentsApi } from "api/payments/paymentsApi";
import { useToaster } from "utils";

interface KsefXmlModalProps {
  onCloseRequest: () => void;
  payment: PaymentApi | null;
  courseId?: number;
}

function formatXml(xmlString: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "text/xml");
    const parseError = doc.querySelector("parsererror");
    if (parseError) return xmlString;
    return serializeNode(doc, 0).trim();
  } catch {
    return xmlString;
  }
}

function serializeNode(node: Node, depth: number): string {
  const pad = "  ".repeat(depth);

  if (node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
    const pi = node as ProcessingInstruction;
    return `${pad}<?${pi.target} ${pi.data}?>`;
  }

  if (node.nodeType === Node.DOCUMENT_NODE) {
    return Array.from(node.childNodes)
      .map((c) => serializeNode(c, depth))
      .filter(Boolean)
      .join("\n");
  }

  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? "").trim();
    return text ? `${pad}${text}` : "";
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const el = node as Element;
  const tag = el.tagName;
  const attrs = Array.from(el.attributes)
    .map((a) => `${a.name}="${a.value}"`)
    .join(" ");
  const openTag = attrs ? `<${tag} ${attrs}>` : `<${tag}>`;
  const children = Array.from(el.childNodes);

  if (children.length === 0) {
    return `${pad}<${tag}${attrs ? " " + attrs : ""} />`;
  }

  if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
    const text = (children[0].textContent ?? "").trim();
    if (text) return `${pad}${openTag}${text}</${tag}>`;
    return `${pad}<${tag}${attrs ? " " + attrs : ""} />`;
  }

  const inner = children
    .map((c) => serializeNode(c, depth + 1))
    .filter(Boolean)
    .join("\n");

  return `${pad}${openTag}\n${inner}\n${pad}</${tag}>`;
}

function highlightXml(xml: string): string {
  return xml
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /(&lt;\?xml[^?]*\?&gt;)/g,
      '<span class="text-slate-400 italic">$1</span>',
    )
    .replace(
      /(&lt;\/?)([\w:.-]+)/g,
      '$1<span class="text-blue-600 font-medium dark:text-blue-400">$2</span>',
    )
    .replace(
      /([\w:.-]+)=&quot;([^&]*)&quot;/g,
      '<span class="text-amber-600 dark:text-amber-400">$1</span>=<span class="text-green-700 dark:text-green-400">&quot;$2&quot;</span>',
    )
    .replace(/(&gt;)([^&<\n]+)(&lt;\/)/g, (_, open, text, close) => {
      const trimmed = text.trim();
      if (!trimmed) return open + text + close;
      return `${open}<span class="text-slate-700 dark:text-slate-300">${text}</span>${close}`;
    });
}

export const KsefXmlModal = ({ onCloseRequest, payment, courseId }: KsefXmlModalProps) => {
  const { xml, isLoading, invalidate } = useKsefXml(payment?.id, courseId);
  const [editedXml, setEditedXml] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccess, showError } = useToaster();

  useEffect(() => {
    setEditedXml(xml ? formatXml(xml) : "");
  }, [xml]);

  useEffect(() => {
    if (!payment) {
      setIsEditing(false);
      invalidate();
    }
  }, [payment]);

  const handleGenerate = async () => {
    if (!payment) return;
    setIsGenerating(true);
    try {
      const result = await paymentsApi.generateKsefXml(payment.id, courseId);
      const generated = result.data?.xml ?? "";
      setEditedXml(generated ? formatXml(generated) : "");
      invalidate();
      showSuccess("XML KSeF wygenerowany pomyślnie");
    } catch {
      showError("Nie udało się wygenerować XML KSeF");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmitToKsef = async () => {
    if (!payment) return;
    setIsSubmitting(true);
    try {
      await paymentsApi.submitToKsef(payment.id, courseId);
      showSuccess("Wysyłka do KSeF zlecona");
    } catch {
      showError("Nie udało się zlecić wysyłki do KSeF");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!payment) return;
    setIsSaving(true);
    try {
      await paymentsApi.updateKsefXml(payment.id, editedXml || null, courseId);
      setIsEditing(false);
      invalidate();
      showSuccess("XML KSeF zapisany");
    } catch {
      showError("Nie udało się zapisać XML KSeF");
    } finally {
      setIsSaving(false);
    }
  };

  const isBusy = isGenerating || isSaving || isSubmitting;

  return (
    <PaziaModal
      id="ksef-xml-modal"
      onCloseRequest={onCloseRequest}
      isOpen={payment != null}
      size="4xl"
      className="flex max-h-[90vh] flex-col overflow-hidden p-6"
    >
      <div className="mb-4 flex flex-shrink-0 items-center justify-between">
        <h2 className="text-xl font-semibold">KSeF XML (FA3)</h2>
        {editedXml && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-blue-600 hover:underline dark:text-blue-400"
          >
            Edytuj
          </button>
        )}
        {isEditing && (
          <button
            onClick={() => { setIsEditing(false); setEditedXml(xml ? formatXml(xml) : ""); }}
            className="text-sm text-slate-500 hover:underline"
          >
            Anuluj edycję
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-slate-600" />
            <p className="text-gray-600 dark:text-slate-400">Ładowanie XML...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4 overflow-hidden">
          {isEditing ? (
            <textarea
              className="min-h-[400px] flex-1 resize-none rounded border border-blue-400 bg-slate-50 p-3 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-blue-300 dark:bg-slate-900 dark:text-slate-200"
              value={editedXml}
              onChange={(e) => setEditedXml(e.target.value)}
              spellCheck={false}
            />
          ) : editedXml ? (
            <pre
              className="flex-1 overflow-auto rounded border border-gray-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed dark:border-slate-700 dark:bg-slate-900"
              dangerouslySetInnerHTML={{ __html: highlightXml(editedXml) }}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400 dark:border-slate-600">
              Brak wygenerowanego XML.<br />Kliknij &quot;Generuj&quot; aby wygenerować XML z konfiguracji faktury.
            </div>
          )}

          <div className="flex flex-shrink-0 items-center justify-end gap-2">
            <PaziaButton
              type="secondary"
              title={isGenerating ? "Generowanie..." : "Generuj"}
              onClick={handleGenerate}
              disabled={isBusy}
            />
            <PaziaButton
              type="danger"
              title={isSubmitting ? "Wysyłanie..." : "Wyślij do KSeF"}
              onClick={handleSubmitToKsef}
              disabled={isBusy || !editedXml}
            />
            {isEditing && (
              <PaziaButton
                type="primary"
                title={isSaving ? "Zapisywanie..." : "Zapisz"}
                onClick={handleSave}
                disabled={isBusy}
              />
            )}
          </div>
        </div>
      )}
    </PaziaModal>
  );
};
